import { useMemo, useState } from "react";
import { Check, ChevronDown, Link2, Search, X } from "lucide-react";

import {
  useAddBoardDependencies,
  useBoardFeatures,
  useFeatureDependencyIds,
  useRemoveBoardDependencies,
} from "@/application/use-cases/board/use-board.queries";
import { StatusDot } from "@/presentation/components/status/status-dot";
import { DELIVERY_STATUS_LABEL, DELIVERY_STATUS_TONE } from "@/presentation/data/board";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@monorepo-apps/ui/src/components/popover";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type {
  BoardFeatureRow as BoardFeature,
  MvpComponentRow,
} from "@/infrastructure/supabase/database.types";

interface DependencyPickerProps {
  feature: BoardFeature;
  componentsById: Map<string, MvpComponentRow>;
  workstreamCodeByKey: Map<string, string>;
  /**
   * "inline" renders the list in-flow (used inside the Sheet, where a portaled
   * popover can't wheel-scroll under react-remove-scroll). "popover" renders a
   * portaled dropdown (used in the board table, outside any Sheet).
   */
  variant?: "inline" | "popover";
  /** view-only: renders prerequisites as a static list, no editing */
  readOnly?: boolean;
}

interface Group {
  key: string;
  label: string;
  items: BoardFeature[];
}

const NO_COMPONENT = "__none__";
const EMPTY_FEATURES: BoardFeature[] = [];

export function DependencyPicker({
  feature,
  componentsById,
  workstreamCodeByKey,
  variant = "popover",
  readOnly = false,
}: DependencyPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Only subscribe to the features cache while open (popover) or always for the
  // inline drawer variant, which shows chip titles even when collapsed.
  const allFeatures =
    useBoardFeatures({ subscribed: open || variant === "inline" }).data ?? EMPTY_FEATURES;
  const idsArray = useFeatureDependencyIds(feature.id);
  const selectedIds = useMemo(() => new Set(idsArray), [idsArray]);
  const addDeps = useAddBoardDependencies();
  const removeDeps = useRemoveBoardDependencies();
  const onAdd = (ids: string[]) => {
    if (readOnly) return;
    addDeps.mutate({ featureId: feature.id, dependsOnIds: ids });
  };
  const onRemove = (ids: string[]) => {
    if (readOnly) return;
    removeDeps.mutate({ featureId: feature.id, dependsOnIds: ids });
  };

  // Candidates/groups are only needed while the dropdown is open, so closed
  // pickers stay near-free even though every feature edit re-renders them.
  const candidates = useMemo(
    () => (open ? allFeatures.filter((f) => f.id !== feature.id) : EMPTY_FEATURES),
    [open, allFeatures, feature.id]
  );

  const groups = useMemo<Group[]>(() => {
    if (!open) return [];
    const q = search.trim().toLowerCase();
    const byKey = new Map<string, BoardFeature[]>();
    for (const f of candidates) {
      const comp = f.component_id ? componentsById.get(f.component_id) : undefined;
      if (q) {
        const hay = `${f.title} ${comp?.name_en ?? ""}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const key = f.component_id ?? NO_COMPONENT;
      const list = byKey.get(key);
      if (list) list.push(f);
      else byKey.set(key, [f]);
    }
    const result: Group[] = [];
    for (const [key, items] of byKey.entries()) {
      const comp = key === NO_COMPONENT ? undefined : componentsById.get(key);
      result.push({
        key,
        label: comp?.name_en ?? "No component",
        items: items.sort((a, b) => a.sort_order - b.sort_order),
      });
    }
    return result.sort((a, b) => {
      const sa = a.key === NO_COMPONENT ? Infinity : (componentsById.get(a.key)?.sort_order ?? 0);
      const sb = b.key === NO_COMPONENT ? Infinity : (componentsById.get(b.key)?.sort_order ?? 0);
      return sa - sb;
    });
  }, [open, candidates, componentsById, search]);

  const showChips = open || variant === "inline";
  const selectedFeatures = useMemo(
    () => (showChips ? allFeatures.filter((f) => selectedIds.has(f.id)) : EMPTY_FEATURES),
    [showChips, allFeatures, selectedIds]
  );

  const toggleOne = (id: string) => {
    if (selectedIds.has(id)) onRemove([id]);
    else onAdd([id]);
  };

  const toggleGroup = (g: Group) => {
    const ids = g.items.map((f) => f.id);
    const allSelected = ids.every((id) => selectedIds.has(id));
    if (allSelected) onRemove(ids);
    else onAdd(ids.filter((id) => !selectedIds.has(id)));
  };

  const list = (
    <>
      <div className="border-border/60 bg-card sticky top-0 z-[2] border-b">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search features…"
            className="h-9 border-0 pl-8 text-[12.5px]"
          />
        </div>
      </div>
      {groups.length === 0 ? (
        <p className="text-muted-foreground px-3 py-4 text-center text-[12px]">
          No matching features.
        </p>
      ) : (
        groups.map((g) => {
          const ids = g.items.map((f) => f.id);
          const selCount = ids.filter((id) => selectedIds.has(id)).length;
          const allSel = selCount === ids.length;
          return (
            <div key={g.key} className="flex flex-col">
              <button
                type="button"
                onClick={() => toggleGroup(g)}
                className="bg-muted border-border/60 hover:bg-muted/80 sticky top-9 z-[1] flex items-center justify-between gap-2 border-b px-3 py-1.5 text-left"
              >
                <span className="font-data text-muted-foreground text-[10px] tracking-[0.12em] uppercase">
                  {g.label}
                </span>
                <span
                  className={cn(
                    "font-data text-[9.5px] tracking-[0.1em] uppercase",
                    allSel ? "text-primary" : "text-muted-foreground/70"
                  )}
                >
                  {selCount > 0 ? `${selCount}/${ids.length} · ` : ""}
                  {allSel ? "All" : "Select all"}
                </span>
              </button>
              {g.items.map((f) => {
                const checked = selectedIds.has(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleOne(f.id)}
                    className="hover:bg-muted/50 flex items-center gap-2 px-3 py-1.5 text-left text-[12.5px]"
                  >
                    <span
                      title={DELIVERY_STATUS_LABEL[f.delivery_status]}
                      className="flex shrink-0"
                    >
                      <StatusDot tone={DELIVERY_STATUS_TONE[f.delivery_status]} size="md" />
                    </span>
                    <span className="line-clamp-1 flex-1">{f.title}</span>
                    <span className="font-data text-muted-foreground/70 shrink-0 text-[9px] tracking-[0.1em] uppercase">
                      {workstreamCodeByKey.get(f.workstream_key) ?? ""}
                    </span>
                    {checked ? <Check className="text-primary size-3.5 shrink-0" /> : null}
                  </button>
                );
              })}
            </div>
          );
        })
      )}
    </>
  );

  const chips =
    selectedFeatures.length > 0 ? (
      <ul className="flex flex-col gap-1">
        {selectedFeatures.map((f) => (
          <li
            key={f.id}
            className="border-border/60 bg-muted/30 flex items-center gap-2 rounded-md border px-2 py-1.5 text-[12.5px]"
          >
            <span title={DELIVERY_STATUS_LABEL[f.delivery_status]} className="flex shrink-0">
              <StatusDot tone={DELIVERY_STATUS_TONE[f.delivery_status]} size="md" />
            </span>
            <span className="line-clamp-1 flex-1">{f.title}</span>
            {readOnly ? null : (
              <button
                type="button"
                onClick={() => onRemove([f.id])}
                className="text-muted-foreground hover:text-destructive shrink-0 transition-colors"
                aria-label={`Remove prerequisite ${f.title}`}
              >
                <X className="size-3" />
              </button>
            )}
          </li>
        ))}
      </ul>
    ) : null;

  if (readOnly) {
    // Static, non-interactive view for both variants — no add/remove/search.
    if (variant === "inline") {
      return chips ?? <p className="text-muted-foreground text-[12px] italic">No prerequisites.</p>;
    }
    return (
      <span
        className="font-data text-muted-foreground/70 flex h-8 w-full items-center justify-center gap-1 text-[12px]"
        title={selectedIds.size > 0 ? `${selectedIds.size} prerequisite(s)` : "No prerequisites"}
      >
        <Link2 className="size-3.5" />
        {selectedIds.size > 0 ? selectedIds.size : "—"}
      </span>
    );
  }

  if (variant === "inline") {
    return (
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          type="button"
          className="h-9 w-full justify-between"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="truncate text-[12px]">
            {selectedIds.size === 0
              ? "Add a prerequisite…"
              : `${selectedIds.size} prerequisite${selectedIds.size === 1 ? "" : "s"}`}
          </span>
          <ChevronDown
            className={cn("size-3.5 opacity-60 transition-transform", open && "rotate-180")}
          />
        </Button>
        {open ? (
          <div className="border-border/60 max-h-[320px] overflow-y-auto rounded-md border">
            {list}
          </div>
        ) : null}
        {chips}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Edit dependencies"
          className={cn(
            "font-data flex h-8 w-full items-center justify-center gap-1 rounded-sm border border-transparent px-2 text-[12px] transition",
            selectedIds.size > 0
              ? "text-foreground hover:border-border hover:bg-muted/40"
              : "text-muted-foreground/50 hover:text-muted-foreground hover:border-border hover:bg-muted/40"
          )}
        >
          <Link2 className="size-3.5" />
          {selectedIds.size > 0 ? selectedIds.size : "—"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <div className="max-h-[380px] overflow-y-auto">{list}</div>
        {chips ? <div className="border-border/60 border-t p-2">{chips}</div> : null}
      </PopoverContent>
    </Popover>
  );
}
